/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { ArchiveEntityType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ArchiveLogProps {
  id: string;
  archiveEntityType: ArchiveEntityType;
  archivedUntilDate: Date;
  isError: boolean;
  notes: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ArchiveLogRecord extends Record<ArchiveLogProps>({
  id: null as any,
  archiveEntityType: null as any,
  archivedUntilDate: null as any,
  isError: null as any,
  notes: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    archiveEntityType: ArchiveEntityType;
    archivedUntilDate: string | number | Date;
    isError: boolean;
    notes: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ArchiveLogRecord {
    if (json == null) {
      throw new Error('ArchiveLogRecord.fromJS: parameter cannot be null');
    }
    return new ArchiveLogRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ArchiveLogJson.id',
      ),
      archiveEntityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'archiveEntityType', ArchiveEntityType.CardNote),
        'ArchiveLogJson.archiveEntityType',
      ),
      archivedUntilDate: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'archivedUntilDate', '0001-01-01T00:00:00.00Z'),
          'ArchiveLogJson.archivedUntilDate',
        ),
      ),
      isError: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isError', false),
        'ArchiveLogJson.isError',
      ),
      notes: json.notes,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    archiveEntityType: ArchiveEntityType;
    archivedUntilDate: string | number | Date;
    isError: boolean;
    notes: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ArchiveLogRecord, Error[]] {
    if (json == null) {
      throw new Error('ArchiveLogRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ArchiveLogRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ArchiveLogJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      archiveEntityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'archiveEntityType', ArchiveEntityType.CardNote),
            'ArchiveLogJson.archiveEntityType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      archivedUntilDate: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'archivedUntilDate', '0001-01-01T00:00:00.00Z'),
              'ArchiveLogJson.archivedUntilDate',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isError: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isError', false),
            'ArchiveLogJson.isError',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ArchiveLogProps) {
    super(props);
    if (props == null) {
      throw new Error('ArchiveLogRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ArchiveLogViewModelBase implements ArchiveLogProps {
  protected readonly _props: ArchiveLogProps;
  private _json?: ArchiveLogProps;

  get id(): string {
    return this._props.id;
  }
  get archiveEntityType(): ArchiveEntityType {
    return this._props.archiveEntityType;
  }
  get archivedUntilDate(): Date {
    return this._props.archivedUntilDate;
  }
  get isError(): boolean {
    return this._props.isError;
  }
  get notes(): string | null {
    return this._props.notes;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCardNote() {
    return this._props.archiveEntityType === ArchiveEntityType.CardNote;
  }
  public get isEta() {
    return this._props.archiveEntityType === ArchiveEntityType.Eta;
  }
  public get isPositionReport() {
    return this._props.archiveEntityType === ArchiveEntityType.PositionReport;
  }
  public get isTimestamp() {
    return this._props.archiveEntityType === ArchiveEntityType.Timestamp;
  }
  public get isTransportOffer() {
    return this._props.archiveEntityType === ArchiveEntityType.TransportOffer;
  }
  public get isWebhookEventBody() {
    return this._props.archiveEntityType === ArchiveEntityType.WebhookEventBody;
  }
  public get isWebhookSubscription() {
    return this._props.archiveEntityType === ArchiveEntityType.WebhookSubscription;
  }
  public get isWebhookSubscriptionEvent() {
    return this._props.archiveEntityType === ArchiveEntityType.WebhookSubscriptionEvent;
  }

  public isAnyArchiveEntityType(...archiveEntityType: ArchiveEntityType[]) {
    return archiveEntityType.includes(this._props.archiveEntityType);
  }

  constructor(props: ArchiveLogProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        archiveEntityType: this._props.archiveEntityType,
        archivedUntilDate: this._props.archivedUntilDate,
        isError: this._props.isError,
        notes: this._props.notes,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllArchiveLogEntities = (state: AppState) =>
  state.entityStores.archiveLog.entities;

export const selectArchiveLogById = (id: string) => (state: AppState) =>
  state.entityStores.archiveLog.entities.get(id);

export const selectArchiveLogMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.archiveLog.meta.get(id);

export const selectArchiveLogAndMetadataById = (id: string) =>
  createSelector(
    selectArchiveLogById(id),
    selectArchiveLogMetadataById(id),
    (archiveLog, meta) => ({ archiveLog, meta }),
  );

export function selectArchiveLogEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllArchiveLogEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ArchiveLogRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectArchiveLogEntitiesByArchiveEntityType(archiveEntityType: ArchiveEntityType) {
  return createSelector(selectAllArchiveLogEntities, (entities) =>
    entities.filter((e) => e.archiveEntityType === archiveEntityType).valueSeq(),
  );
}

export function selectArchiveLogEntitiesByArchivedUntilDate(archivedUntilDate: Date) {
  return createSelector(selectAllArchiveLogEntities, (entities) =>
    entities.filter((e) => e.archivedUntilDate === archivedUntilDate).valueSeq(),
  );
}

export function selectArchiveLogEntitiesByIsError(isError: boolean) {
  return createSelector(selectAllArchiveLogEntities, (entities) =>
    entities.filter((e) => e.isError === isError).valueSeq(),
  );
}

export function selectArchiveLogEntitiesByNotes(notes: string) {
  return createSelector(selectAllArchiveLogEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}
//#endregion Selectors
